<template>
  <PullUpDeviceGuidePlaceHolder v-bind="$attrs">
    <template #left-icon>
      <img class="page-img" src="@assets/svgs/bg-guide-connect-device.svg" alt="" />
    </template>
    <template #right-content>
      <div class="page-content-text">
        <div class="page-content-model">{{ $t('owork_base.pull_device.input_password') }}</div>
        <div class="page-content-text-describe">{{ $t('owork_base.pull_device.set_password_tips') }}</div>
        <a-form class="page-content-form">
          <a-form-item v-bind="validateInfos.pushPassword">
            <a-input-password
              v-model:value="formState.pushPassword"
              :placeholder="$t('owork_base.pull_device.password_input_placeholder')"
              :visibility-toggle="false"
              :allow-clear="isInvalidatedPwd || !isStandardScreen"
              @keyup.enter="onSubmit"
            >
              <template v-if="isInvalidatedPwd" #suffix>
                <img class="input-clear" src="@assets/svgs/icon-input-clear.svg" alt="" @click="reset" />
              </template>
            </a-input-password>
          </a-form-item>
          <a-button :disabled="isConnecting || !formState.pushPassword" @click.prevent="onSubmit">{{
            isConnecting ? $t('owork_base.pull_device.connecting') : $t('owork_base.confirm')
          }}</a-button>
        </a-form>
        <i18n-t
          tag="div"
          class="page-tips"
          keypath="owork_base.pull_device.tips_switch_to_device_confirm_connection_slot"
        >
          <template #tips_switch_to_device_confirm_connection_slot_value>
            <span class="page-device-toggle-btn" @click="emits(ConnectionGuideEvents.TOGGLE_CONNECT_TYPE)">{{
              $t('owork_base.pull_device.tips_switch_to_device_confirm_connection_slot_value')
            }}</span>
          </template>
        </i18n-t>
      </div>
    </template>
  </PullUpDeviceGuidePlaceHolder>
</template>

<script lang="ts" setup>
  import type { Rule } from 'ant-design-vue/lib/form'
  import { useForm } from 'ant-design-vue/lib/form'
  import { reactive, ref, watch, onBeforeMount } from 'vue'
  import { INSTANCE as DevicePullUpTaskManager } from '@core/DevicePull/DevicePullUpTaskManager'
  import { DEVICE_PULL_UP_TIPS_MAP, DEVICE_PULL_UP_TYPE } from '@/netadapters/CloudAdapter'
  import { PullUpDeviceState, PullUpDeviceType } from '@core/DevicePull/DevicePullUpTask'
  import { ConnectionGuideEvents } from '@components/Constants'
  import PullUpDeviceGuidePlaceHolder from '@components/pullUpDeviceGuide/PullUpDeviceGuidePlaceHolder.vue'
  import { TrackEventMap } from '@core/TrackConstants'
  import { usePullUpDeviceTrack } from '@core/compositions/PullUpDeviceTrack'
  import type { PullDeviceTrackInfo } from '@core/compositions/PullUpDeviceTrack'
  import { I18nT, useI18n } from 'vue-i18n'
  import { useSharedScreen } from '@/core/compositions/Screen'

  const { isStandardScreen } = useSharedScreen()
  const { t } = useI18n()

  interface FormState {
    pushPassword: string
  }

  const props = defineProps<{
    deviceId: string
  }>()

  const emits = defineEmits<{
    (e: ConnectionGuideEvents.SHOW_TOAST, tips?: string): void
    (e: ConnectionGuideEvents.TOGGLE_CONNECT_TYPE): void
  }>()

  const isConnecting = ref(false)
  const isInvalidatedPwd = ref(false)

  const formState = reactive<FormState>({
    pushPassword: '',
  })

  watch(
    () => formState.pushPassword,
    (newValue) => {
      if (!newValue) {
        reset()
      }
    }
  )

  const reset = () => {
    resetFields()
    isInvalidatedPwd.value = false
  }
  const trackInfo: PullDeviceTrackInfo = usePullUpDeviceTrack(TrackEventMap.PULL_UP_DEVICE_BY_PASSWORD)

  const validatePass = (_rule: Rule) =>
    new Promise((resolve, reject) => {
      if (!isConnecting.value) {
        isConnecting.value = true

        DevicePullUpTaskManager.pullUpDevice(
          {
            deviceId: props.deviceId,
            type: PullUpDeviceType.BY_PASSWORD,
            ...formState,
          },
          {
            onPullSuccess: () => {
              trackInfo.connectSucceed = true
              resolve(true)
            },
            onPullTimeout: () => {
              const tips = DEVICE_PULL_UP_TIPS_MAP.get(DEVICE_PULL_UP_TYPE.CONNECTED_TIMEOUT)

              isConnecting.value = false
              trackInfo.connectSucceed = false

              emits(ConnectionGuideEvents.SHOW_TOAST, tips)
            },
            onPullFailed: (deviceId: string, state, errorCode?: DEVICE_PULL_UP_TYPE) => {
              const result = errorCode

              if (result) {
                switch (result) {
                  case DEVICE_PULL_UP_TYPE.WRONG_PASSWORD:
                    trackInfo.enterPasswordSucceed = false
                    trackInfo.initConnectionSucceed = false
                    reject(t('owork_base.pull_device.wrong_password'))
                    break

                  case DEVICE_PULL_UP_TYPE.UNSET_PASSWORD:
                    trackInfo.enterPasswordSucceed = false
                    trackInfo.initConnectionSucceed = false
                    reject(t('owork_base.pull_device.not_set_password'))
                    break
                  default:
                    const tips =
                      DEVICE_PULL_UP_TIPS_MAP.get(result) ??
                      DEVICE_PULL_UP_TIPS_MAP.get(DEVICE_PULL_UP_TYPE.NORMAL_ERROR)

                    emits(ConnectionGuideEvents.SHOW_TOAST, tips)

                    break
                }

                reject()
              }
              trackInfo.connectSucceed = false
            },
            onPullDeviceStateChange(deviceId, state) {
              if (state === PullUpDeviceState.ON_STARTED_PULL_UP_DEVICE) {
                trackInfo.initConnectionSucceed = true
                trackInfo.enterPasswordSucceed = true
                isInvalidatedPwd.value = false
                resolve(true)
              }
            },
          },
          false,
          true,
          false
        )
      } else {
        reject()
      }
    })

  const rulesRef = reactive({
    pushPassword: [
      {
        required: true,
        validator: validatePass,
        trigger: 'submit',
      },
    ],
  })
  const { validate, validateInfos, resetFields } = useForm(formState, rulesRef)

  const onSubmit = () => {
    validate()
      .then(() => {
        //do nothing
      })
      .catch(() => {
        isInvalidatedPwd.value = true
        isConnecting.value = false
      })
  }

  onBeforeMount(() => {
    isConnecting.value = false
  })
</script>

<style lang="stylus" scoped>
  @import '../styles/pullUpDeviceGuide.styl'
  .ant-input-affix-wrapper
  .ant-input
    border-radius 8px
    rect(100%, 36px)
    font-size 14px
    box-sizing border-box
    border-color active_color
    caret-color active_color !important
  .input-clear
    rect(21px)
  .page-content-text
    margin-top 100px
    .page-content-text-describe
      line-height 19.88px
    .page-tips
      line-height 17.04px

  @import '../styles/h5Adaptive/GuidePopperComponent.styl'

  @media screen and (max-width h5_width_threshold )
    .isFileManager
      .page
        .page-content
          .page-content-container
            .page-content-text
              margin-top 56px
              .page-content-text-describe
                margin-top 12px
                color h5_default_color
                font-weight h5_default_font_weight
              .page-tips
                color h5_default_shallow_color
                padding 0 16px
                text-align center
                margin-bottom 72px
        .ant-input-affix-wrapper
        .ant-input
          border-radius 12px
</style>
